import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import {
  FlexColumn,
  FlexRow,
  SMALL_PRECISION,
  TableData,
  Text,
  useMatchMedia,
} from '@make-software/cspr-design';
import { AccountInfoAccordion } from '../account-info-accordion/account-info-accordion';
import { BodyText, Table } from '@block-explorer/ui';
import DetailsTableRow from '../details-table-row/details-table-row';
import ExpandCollapsedButton from '../expand-collapsed/expand-collapsed-button';
import { useCustomHistory } from '../../router/navigation';
import { TableDataCep18TokenInfo } from '../table-data-cep18-token-info/table-data-cep18-token-info';
import FiatCurrency from '../multi-currency/multi-currency';
import { formatTokenAmount } from '../../utils/ft-token-amount';

import styled from 'styled-components';
import DetailsValueTableData from '../details-value-table-data/details-value-table-data';
import { buildRouterPath, useRouterPath } from '../../router/utils';
import { CONTRACT_PACKAGE_TAB_PATH } from '../../router/paths';
import { BalanceType, TokensBalanceSummary } from '../../services';

const StyledDetailsValueTableData = styled(DetailsValueTableData)(
  ({ theme }) => ({
    '&&': {
      padding: '16px 0',
    },
  })
);

const StyledTableFooterData = styled.td(({ theme }) => ({
  width: '100%',
  padding: '8px 0',
}));

const MAXIMUM_VISIBLE_ROWS = 2;

export const DetailsAccordionContractCep18TokensFeed = ({
  tokens,
  type,
  balance,
}: {
  tokens: TokensBalanceSummary[];
  type: BalanceType;
  balance: string;
}) => {
  const { t } = useTranslation();
  const history = useCustomHistory();
  const {
    urlParams: { id },
  } = useRouterPath(CONTRACT_PACKAGE_TAB_PATH);

  const minifiedForMobile = useMatchMedia([true, false], []);

  const handleExpandList = () => {
    history.push(
      buildRouterPath(
        CONTRACT_PACKAGE_TAB_PATH,
        { id, tab: 'assets' },
        undefined
      )
    );
  };

  return (
    <DetailsTableRow>
      <TableData>
        <AccountInfoAccordion
          withFeed
          isOpen={!minifiedForMobile}
          disableClickAway
          renderContent={() => (
            <Table
              renderData={() => (
                <>
                  {tokens
                    .filter((ftToken, i) => i < MAXIMUM_VISIBLE_ROWS)
                    .map((ftToken, index) => {
                      return (
                        <DetailsTableRow key={'token-' + index}>
                          <StyledDetailsValueTableData>
                            <FlexColumn gap={8}>
                              <FlexRow>
                                {ftToken.symbol && (
                                  <TableDataCep18TokenInfo
                                    data={ftToken?.contractPackage}
                                    symbol={ftToken.symbol}
                                    loading={false}
                                  />
                                )}
                              </FlexRow>
                              <FlexRow gap={8}>
                                <BodyText size={2} monotype>
                                  {formatTokenAmount(
                                    ftToken.tokenAmount,
                                    SMALL_PRECISION,
                                    false
                                  )}
                                </BodyText>
                                {ftToken.tokenBalanceInCurrency &&
                                  ftToken.tokenBalanceInCurrency !== '0' && (
                                    <BodyText
                                      size={3}
                                      variation={'darkGray'}
                                      monotype
                                      noWrap
                                    >
                                      (
                                      <FiatCurrency
                                        amount={ftToken.tokenBalanceInCurrency}
                                        precision={SMALL_PRECISION}
                                      />
                                      )
                                    </BodyText>
                                  )}
                              </FlexRow>
                            </FlexColumn>
                          </StyledDetailsValueTableData>
                        </DetailsTableRow>
                      );
                    })}
                  {tokens?.length > MAXIMUM_VISIBLE_ROWS && (
                    <DetailsTableRow key={'view-all-cep18'}>
                      <StyledTableFooterData>
                        <ExpandCollapsedButton
                          showArrow={false}
                          onExpand={handleExpandList}
                          collapsedLabel={<Trans t={t}>View all tokens</Trans>}
                        />
                      </StyledTableFooterData>
                    </DetailsTableRow>
                  )}
                </>
              )}
            />
          )}
        >
          <FlexRow gap={6}>
            <Trans t={t}>Tokens</Trans>{' '}
            <Text variation={'darkGray'}>
              &middot; {type !== BalanceType.TOKENS_MARKET_DATA_BALANCE && '>'}
              <FiatCurrency amount={balance} precision={SMALL_PRECISION} />
            </Text>{' '}
          </FlexRow>
        </AccountInfoAccordion>
      </TableData>
    </DetailsTableRow>
  );
};
